import { state, settings, FREE_MODE_LIMIT } from './shared/state';
import { $, t, showToast } from './shared/index';
import { sessionStore, ensureMessageId, normalizeMessages } from './session/session-store';
import { SessionManager, registerActiveSessionSwitchedHandler } from './session/sessions';
import { registerGeneratingCheck } from './shared/app-hooks';
import { showRegistrationPrompt } from './shared/navigation';

const isGuestMode = () => localStorage.getItem('avgexpert_guest_mode') === '1';

// --- Conversation view adapter (ADR-002 phase C): the generation engine must not
// import the conversation feature; conversation/conversation.ts registers its rendering here on
// module evaluation.
export interface ConversationViewAdapter {
  addMessageToUI(
    role: ConversationMessage['role'],
    text: string,
    docs: AttachedDoc[],
    categoryName: string,
    messageId?: string,
  ): HTMLElement | null;
  renderActiveConversationView(): void;
  buildExtraParams(): Record<string, unknown> | null;
}

let conversationView: ConversationViewAdapter | null = null;

export function registerConversationView(adapter: ConversationViewAdapter): void {
  conversationView = adapter;
}

import {
  updateContextBadge,
  showTypingIndicator,
  autoResizeTextarea,
  renderMarkdown,
  setWelcomeVisible,
} from './shared/ui';
import { hasPendingAttachments } from './session/session-attachments';
import { isLargeRequest, confirmLargeRequest } from './billing/large-request';
import type { AttachedDoc, CategoryData, ConversationMessage } from './shared/types';

const STREAM_RENDER_INTERVAL_MS = 60;
const STREAM_RENDER_MIN_NEW_CHARS = 24;

function createStreamMarkdownRenderer(el: HTMLElement, onRender?: () => void) {
  let lastRenderAt = 0;
  let lastRenderedLength = 0;
  let pendingText = '';
  let renderTimer: ReturnType<typeof setTimeout> | null = null;
  let rafPending = false;

  const render = (force = false) => {
    renderTimer = null;
    const newChars = pendingText.length - lastRenderedLength;
    if (!force && newChars < STREAM_RENDER_MIN_NEW_CHARS) {
      renderTimer = setTimeout(() => render(true), STREAM_RENDER_INTERVAL_MS * 2);
      return;
    }
    lastRenderAt = Date.now();
    lastRenderedLength = pendingText.length;
    renderMarkdown(el, pendingText);
    if (onRender && !rafPending) {
      rafPending = true;
      requestAnimationFrame(() => { rafPending = false; onRender(); });
    }
  };

  return {
    schedule(text: string) {
      pendingText = text;
      const delay = Math.max(0, STREAM_RENDER_INTERVAL_MS - (Date.now() - lastRenderAt));
      if (!renderTimer) {
        renderTimer = setTimeout(render, delay);
      }
    },
    flush(text = pendingText) {
      pendingText = text;
      if (renderTimer) {
        clearTimeout(renderTimer);
        renderTimer = null;
      }
      render(true);
    },
  };
}

function buildUserContent(text: string, attachedDocs: AttachedDoc[]) {
  const inlineDocs = attachedDocs.filter((d) => d.text && !d.sessionScoped);
  if (inlineDocs.length === 0) return text;
  const docBlock = inlineDocs.map((d, i) => `=== ${state.lang === 'ru' ? 'Документ' : 'Document'} ${i + 1}: ${d.name} ===\n${d.text}`).join('\n\n');
  return docBlock + '\n\n---\n\n' + text;
}

function buildOutgoingMessages(session: ReturnType<typeof sessionStore.getSession>, text: string) {
  const userContent = buildUserContent(text, session!.attachedDocs);
  const inlineDocs = session!.attachedDocs.filter((d) => d.text && !d.sessionScoped);
  const previewHistory: ConversationMessage[] = [...session!.history, ensureMessageId({ role: 'user', content: userContent, docs: inlineDocs.length ? inlineDocs : undefined })];
  const messages: ConversationMessage[] = [{ role: 'system', content: settings.system_prompt }, ...previewHistory];
  return { userContent, messages };
}

function syncSendStopButtons(sessionId?: string) {
  const activeId = sessionStore.activeSessionId;
  if (!activeId) {
    $('send-btn')?.classList.remove('hidden');
    $('stop-btn')?.classList.add('hidden');
    return;
  }
  const generating = sessionStore.isSessionGenerating(activeId);
  $('send-btn')?.classList.toggle('hidden', !!generating);
  $('stop-btn')?.classList.toggle('hidden', !generating);
}

function updateGlobalStatusDot() {
  const statusDot = $('status-dot');
  const statusText = $('status-text');
  if (!statusDot) return;

  const streaming = sessionStore.streamingCount();
  if (streaming > 0) {
    statusDot.className = 'status-dot generating';
    if (statusText && streaming > 1) {
      statusText.textContent = `${streaming} генерируется`;
    }
    return;
  }

  if (statusDot.classList.contains('offline')) return;
  statusDot.className = 'status-dot';
  if (statusText && state.currentUser) {
    statusText.textContent = state.currentUser.username || t('status_online');
  }
}

const JOB_POLL_MS = 2000;

type JobStreamResult = 'done' | 'aborted' | 'error';

async function consumeJobStream(
  jobId: string,
  signal: AbortSignal,
  onPartial: (fullText: string) => void,
): Promise<JobStreamResult> {
  if (!state.authToken && !isGuestMode()) return 'error';

  const response = await fetch(`/api/conversation/generations/${encodeURIComponent(jobId)}/stream`, {
    headers: { Authorization: `Bearer ${state.authToken || 'null'}` },
    signal,
  });

  if (!response.ok || !response.body) return 'error';

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let fullText = '';

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';

      for (const rawLine of lines) {
        const line = rawLine.trim();
        if (!line.startsWith('data: ')) continue;
        const payload = line.slice(6).trim();
        if (!payload) continue;
        if (payload === '[DONE]') {
          onPartial(fullText);
          return 'done';
        }

        try {
          const chunk = JSON.parse(payload) as {
            choices?: Array<{ delta?: { content?: string }; finish_reason?: string | null }>;
          };
          const choice = chunk.choices?.[0];
          const delta = choice?.delta?.content;
          if (delta) {
            fullText += delta;
            onPartial(fullText);
          }
          if (choice?.finish_reason) {
            onPartial(fullText);
            return 'done';
          }
        } catch {
          // ignore malformed SSE chunks
        }
      }
    }
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') return 'aborted';
    return 'error';
  }

  onPartial(fullText);
  return 'done';
}

type ServerJob = {
  id: string;
  sessionId: string;
  status: string;
  partialText?: string;
  assistantMessageId?: string;
};

async function pollServerJob(sessionId: string, jobId: string, categoryName: string) {
  const session = sessionStore.getSession(sessionId);
  if (!session || (!state.authToken && !isGuestMode())) return;

  try {
    const r = await fetch(`/api/conversation/generations/${jobId}`, {
      headers: { Authorization: 'Bearer ' + (state.authToken || 'null') },
    });
    if (!r.ok) return;
    const job = await r.json() as {
      status: string;
      partial_text?: string;
    };

    const partial = job.partial_text || '';
    if (partial) {
      session.generation.partialAssistantText = partial;
      if (sessionStore.activeSessionId === sessionId) {
        const msgEl = document.querySelector('#messages .msg.assistant:last-child .msg-content') as HTMLElement | null;
        if (msgEl && session.generation.status === 'streaming') {
          renderMarkdown(msgEl, partial);
        }
      }
    }

    if (job.status === 'done') {
      clearJobPoll(session);
      await refreshSessionAfterJob(sessionId, categoryName);
    } else if (job.status === 'failed' || job.status === 'cancelled') {
      clearJobPoll(session);
      const errText = partial || (job.status === 'cancelled' ? 'Генерация отменена' : t('error_server'));
      GenerationManager.finishGeneration(sessionId, errText, categoryName);
    }
  } catch {
    /* retry on next tick */
  }
}

function clearJobPoll(session: ReturnType<typeof sessionStore.getSession>) {
  if (!session) return;
  if (session.generation.pollTimer) {
    clearInterval(session.generation.pollTimer);
    session.generation.pollTimer = null;
  }
}

function mergeMessageDocsFromLocal(
  serverMessages: ConversationMessage[],
  localMessages: ConversationMessage[],
): ConversationMessage[] {
  return serverMessages.map((msg) => {
    if (!msg.id || msg.docs?.length) return msg;
    const local = localMessages.find((m) => m.id === msg.id);
    if (local?.docs?.length) {
      return { ...msg, docs: local.docs };
    }
    return msg;
  });
}

async function refreshSessionAfterJob(sessionId: string, categoryName: string) {
  try {
    const r = await fetch('/api/sessions/' + sessionId, {
      headers: { Authorization: 'Bearer ' + (state.authToken || 'null') },
    });
    if (r.ok) {
      const data = await r.json() as { messages?: ConversationMessage[]; title?: string };
      const session = sessionStore.getSession(sessionId);
      if (session) {
        const localHistory = session.history;
        const serverHistory = normalizeMessages((data.messages || []).filter((m) => m.role !== 'system'));
        session.history = mergeMessageDocsFromLocal(serverHistory, localHistory);
        session.dirty = false;
        if (data.title) session.title = data.title;
        clearStreamingGenerationState(session);
      }
      if (sessionStore.activeSessionId === sessionId) {
        conversationView?.renderActiveConversationView();
      }
      GenerationManager.finishGeneration(sessionId, '', categoryName);
      return;
    }
  } catch { /* fall through */ }
  const session = sessionStore.getSession(sessionId);
  const text = session?.generation.partialAssistantText || '';
  GenerationManager.finishGeneration(sessionId, text, categoryName);
}

function clearStreamingGenerationState(session: NonNullable<ReturnType<typeof sessionStore.getSession>>) {
  clearJobPoll(session);
  session.generation.abortCtrl = null;
  session.generation.serverJobId = null;
  session.generation.partialAssistantText = '';
  session.generation.status = 'idle';
  sessionStore.removeServerGeneratingSession(session.id);
}

function startJobPolling(sessionId: string, jobId: string, categoryName: string) {
  const session = sessionStore.getSession(sessionId);
  if (!session) return;
  clearJobPoll(session);
  void pollServerJob(sessionId, jobId, categoryName);
  session.generation.pollTimer = setInterval(() => {
    void pollServerJob(sessionId, jobId, categoryName);
  }, JOB_POLL_MS);
}

export const GenerationManager = {
  async refreshLimits() {
    if (!state.authToken && !isGuestMode()) return;
    try {
      const r = await fetch('/api/conversation/limits', {
        headers: { Authorization: 'Bearer ' + (state.authToken || 'null') },
      });
      if (r.ok) {
        const data = await r.json() as { max_parallel_generations?: number };
        if (data.max_parallel_generations) {
          sessionStore.maxParallelGenerations = data.max_parallel_generations;
        }
      }
    } catch {
      /* keep default */
    }
  },

  isGenerating(sessionId?: string | null) {
    return sessionStore.isSessionGenerating(sessionId ?? sessionStore.activeSessionId ?? '');
  },

  async syncServerJobs() {
    if (!state.authToken || isGuestMode()) return;
    try {
      const r = await fetch('/api/conversation/jobs?active=1', {
        headers: { Authorization: 'Bearer ' + state.authToken },
      });
      if (!r.ok) return;
      const data = await r.json() as { jobs?: ServerJob[] };
      const jobs = data.jobs || [];
      sessionStore.setServerGeneratingSessions(jobs.map((j) => j.sessionId));

      for (const job of jobs) {
        const session = sessionStore.getOrCreate(job.sessionId);
        if (session.generation.status === 'streaming') continue;

        session.generation.status = 'streaming';
        session.generation.serverJobId = job.id;
        session.generation.partialAssistantText = job.partialText || '';
        session.generation.startedAt = Date.now();

        if (!session.generation.pollTimer) {
          startJobPolling(job.sessionId, job.id, session.generation.categoryName || '');
        }
      }

      SessionManager.refreshSessionIndicators();
      updateGlobalStatusDot();
    } catch {
      /* ignore */
    }
  },

  async startGeneration(sessionId: string, text: string) {
    const session = sessionStore.getOrCreate(sessionId);
    if (session.generation.status === 'streaming') return;
    if (hasPendingAttachments(sessionId)) {
      showToast('Дождитесь завершения индексации вложений', 'error');
      return;
    }
    if (!state.authToken && !isGuestMode()) return;

    if (isGuestMode()) {
      const activityCount = parseInt(localStorage.getItem('avgexpert_free_activity_count') || '0', 10);
      if (activityCount >= FREE_MODE_LIMIT) {
        showToast(`Вы исчерпали лимит бесплатных запросов (максимум ${FREE_MODE_LIMIT}). Пожалуйста, зарегистрируйтесь.`, 'error');
        showRegistrationPrompt();
        return;
      }
      localStorage.setItem('avgexpert_free_activity_count', String(activityCount + 1));
    }

    await this.refreshLimits();
    if (sessionStore.streamingCount() >= sessionStore.maxParallelGenerations) {
      showToast(`Максимум ${sessionStore.maxParallelGenerations} параллельных генераций`, 'error');
      return;
    }

    const { userContent, messages } = buildOutgoingMessages(session, text);

    if (isLargeRequest(messages)) {
      const activeCategory = $<HTMLSelectElement>('conversation-session-category')?.value || state.currentUser?.category || null;
      const confirmed = await confirmLargeRequest(messages, {
        category: activeCategory,
      });
      if (!confirmed) return;
    }

    const userInput = $<HTMLTextAreaElement>('user-input');
    if (userInput && sessionStore.activeSessionId === sessionId) {
      userInput.value = '';
      session.draftInput = '';
      autoResizeTextarea();
    } else {
      session.draftInput = '';
    }

    const inlineDocs = session.attachedDocs.filter((d) => d.text && !d.sessionScoped);
    const userMsg = ensureMessageId({
      role: 'user' as const,
      content: userContent,
      docs: inlineDocs.length ? inlineDocs : undefined,
      timestamp: Date.now(),
    });
    session.history.push(userMsg);
    session.dirty = true;


    const catSelect = $<HTMLSelectElement>('conversation-session-category');
    let activeCategoryName = '';
    if (catSelect && catSelect.selectedIndex >= 0) {
      activeCategoryName = catSelect.options[catSelect.selectedIndex].text;
    }

    session.generation = {
      status: 'streaming',
      abortCtrl: new AbortController(),
      partialAssistantText: '',
      categoryName: activeCategoryName,
      startedAt: Date.now(),
      serverJobId: null,
      pollTimer: null,
    };

    setWelcomeVisible(false);
    SessionManager.refreshSessionIndicators();

    let contentEl: HTMLElement | null = null;
    let renderStreamMarkdown: ReturnType<typeof createStreamMarkdownRenderer> | null = null;

    if (sessionStore.activeSessionId === sessionId) {
      conversationView?.addMessageToUI('user', text, [], '', userMsg.id);
      const msgEl = conversationView?.addMessageToUI('assistant', '', [], activeCategoryName);
      contentEl = (msgEl?.querySelector('.msg-content') as HTMLElement | null) ?? null;
      if (contentEl) {
        showTypingIndicator(contentEl);
        renderStreamMarkdown = createStreamMarkdownRenderer(contentEl, () => {
          const messagesEl = $('messages');
          if (messagesEl) {
            const distFromBottom = messagesEl.scrollHeight - messagesEl.scrollTop - messagesEl.clientHeight;
            if (distFromBottom < 120) {
              messagesEl.scrollTop = messagesEl.scrollHeight;
            }
          }
        });
      }
      syncSendStopButtons(sessionId);
    }

    updateGlobalStatusDot();

    let fullText = '';

    try {
      const activeCategory = $<HTMLSelectElement>('conversation-session-category')?.value || state.currentUser?.category;

      const response = await fetch('/api/conversation/generations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + (state.authToken || 'null'),
        },
        signal: session.generation.abortCtrl!.signal,
        body: JSON.stringify({
          messages,
          stream: true,
          category: activeCategory,
          session_id: sessionId,
          temperature: $<HTMLInputElement>('feat-creative')?.checked ? 1.2 : settings.temperature,
          top_p: settings.top_p,
          top_k: settings.top_k,
          min_p: settings.min_p,
          repeat_penalty: settings.repeat_penalty,
          extra_params: conversationView?.buildExtraParams() ?? null,
        }),
      });

      if (!response.ok) {
        if (response.status === 429) {
          throw new Error('Превышен лимит параллельных генераций');
        }
        if (response.status === 403) {
          let errorCode = '';
          let detail = t('error_server');
          try {
            const errBody = await response.json() as { error?: { code?: string; message?: string } };
            errorCode = errBody?.error?.code || '';
            detail = errBody?.error?.message || detail;
          } catch { /* ignore */ }

          if (errorCode === 'registration_required') {
            showRegistrationPrompt();
            throw new Error(detail);
          }

          if (errorCode === 'tokens_exhausted' || errorCode === 'user_blocked' || errorCode === 'no_token_quota') {
            const quotaMessage = '⊘ «Доступ ограничен. Баланс исчерпан.»';
            fullText = quotaMessage + ' Пополнить баланс';
            if (contentEl && sessionStore.activeSessionId === sessionId) {
              contentEl.textContent = quotaMessage + ' ';
              const creditsLink = document.createElement('button');
              creditsLink.type = 'button';
              creditsLink.className = 'conversation-inline-link';
              creditsLink.textContent = 'Пополнить баланс';
              creditsLink.addEventListener('click', () => $<HTMLButtonElement>('credits-menu-btn')?.click());
              contentEl.appendChild(creditsLink);
              contentEl.className = 'message-content error-text';
            }
            $('send-btn')?.setAttribute('disabled', 'true');
            $('user-input')?.setAttribute('disabled', 'true');
            this.finishGeneration(sessionId, fullText, activeCategoryName);
            return;
          }
          throw new Error(detail);
        }
        throw new Error('HTTP ' + response.status);
      }

      const job = await response.json() as { job_id: string };
      session.generation.serverJobId = job.job_id;
      void SessionManager.loadList();

      const isActiveSession = sessionStore.activeSessionId === sessionId;
      if (isActiveSession) {
        const streamResult = await consumeJobStream(
          job.job_id,
          session.generation.abortCtrl!.signal,
          (partial) => {
            session.generation.partialAssistantText = partial;
            fullText = partial;
            if (renderStreamMarkdown) {
              renderStreamMarkdown.schedule(partial);
            }
          },
        );

        if (streamResult === 'aborted') return;
        if (streamResult === 'done' && session.generation.status === 'streaming') {
          if (renderStreamMarkdown) renderStreamMarkdown.flush(fullText);
          await refreshSessionAfterJob(sessionId, activeCategoryName);
          return;
        }
      }

      startJobPolling(sessionId, job.job_id, activeCategoryName);

      while (session.generation.status === 'streaming' && session.generation.serverJobId) {
        await new Promise((resolve) => setTimeout(resolve, JOB_POLL_MS));
        if (session.generation.status !== 'streaming') break;
      }
      return;
    } catch (e) {
      if (!(e instanceof DOMException && e.name === 'AbortError')) {
        fullText = fullText || session.generation.partialAssistantText || (e instanceof Error ? e.message : t('error_server'));
        if (renderStreamMarkdown && sessionStore.activeSessionId === sessionId) {
          renderStreamMarkdown.flush(fullText);
        }
      } else if (fullText || session.generation.partialAssistantText) {
        fullText = fullText || session.generation.partialAssistantText;
      }
    }

    if (renderStreamMarkdown && sessionStore.activeSessionId === sessionId) {
      renderStreamMarkdown.flush(fullText);
    }

    this.finishGeneration(sessionId, fullText, activeCategoryName);
  },

  finishGeneration(sessionId: string, fullText: string, categoryName: string) {
    const session = sessionStore.getSession(sessionId);
    if (!session) return;

    const wasStreaming = session.generation.status === 'streaming';
    clearJobPoll(session);
    session.generation.abortCtrl = null;
    session.generation.serverJobId = null;
    session.generation.partialAssistantText = '';
    session.generation.status = 'idle';
    sessionStore.removeServerGeneratingSession(sessionId);

    if (fullText && wasStreaming) {
      session.history.push(ensureMessageId({
        role: 'assistant',
        content: fullText,
        categoryName,
        timestamp: Date.now(),
      }));
      session.dirty = true;
    }

    if (sessionStore.activeSessionId === sessionId) {
      syncSendStopButtons(sessionId);
      updateContextBadge();
    }

    updateGlobalStatusDot();
    SessionManager.refreshSessionIndicators();
    SessionManager.applyDerivedTitle(sessionId);
    void SessionManager.saveSession(sessionId);
    void SessionManager.loadList();
  },

  stopGeneration(sessionId?: string | null) {
    const id = sessionId ?? sessionStore.activeSessionId;
    if (!id) return;
    const session = sessionStore.getSession(id);
    if (!session || session.generation.status !== 'streaming') return;

    const jobId = session.generation.serverJobId;
    session.generation.abortCtrl?.abort();
    if (jobId && (state.authToken || isGuestMode())) {
      void fetch(`/api/conversation/generations/${jobId}/cancel`, {
        method: 'POST',
        headers: { Authorization: 'Bearer ' + (state.authToken || 'null') },
      });
    }
    const partial = session.generation.partialAssistantText;
    const categoryName = session.generation.categoryName;
    this.finishGeneration(id, partial, categoryName);
  },

  onActiveSessionSwitched() {
    conversationView?.renderActiveConversationView();
    syncSendStopButtons(sessionStore.activeSessionId ?? undefined);
    updateGlobalStatusDot();
    updateContextBadge();
  },
};

registerGeneratingCheck(() => GenerationManager.isGenerating());
registerActiveSessionSwitchedHandler(() => GenerationManager.onActiveSessionSwitched());
